import React, { useEffect, useMemo, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { ReactComponent as DownArrow } from '../../Assets/Icons/down-black.svg';
import { Country, countries, preferredCountries } from '../../countries';
import { Label } from '../../SubComponents/Text';
import {
  CustomSelect,
  CustomSelectButton,
  CustomSelectOption,
  CustomSelectOptions,
  PhoneInputContainer,
  PhoneNumberInput,
} from './style';
import { CustomerFormInputChangeEvent } from './CustomerForm';

interface PhoneInputProps {
  name: string;
  error: boolean;
  onChange: (event: CustomerFormInputChangeEvent, country?: string) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  value: string;
  placeholder: string;
  required: boolean;
  disabled: boolean;
  shopCountry: string | undefined;
}

const emptyCountry = {
  name: 'Choose country code',
  dial_code: '',
  emoji: '🏳️',
  code: '',
};

const splitPhoneNumber = (value: string, countryName?: string) => {
  for (const country of countries) {
    if (value.includes(country.dial_code)) {
      let returnedCountry = country;

      if (preferredCountries[country.dial_code]) {
        returnedCountry = preferredCountries[country.dial_code];
      }

      if (countryName && country.name === countryName) {
        returnedCountry = country;
      }

      return {
        country: returnedCountry,
        number: value.substring(country.dial_code.length),
      };
    }
  }

  return { country: emptyCountry, number: value };
};

const PhoneInput = (props: PhoneInputProps) => {
  const {
    name,
    error,
    onChange,
    onBlur,
    value,
    placeholder,
    required,
    disabled,
    shopCountry,
  } = props;

  // Used to overwrite prefered country if dial code is same for multiple countries.
  // This is only in effect after the country code has been updated by the user
  // so it shouldnt have any effect when setting initial values.
  const [countryName, setCountryName] = useState('');
  const countryCode = useMemo(
    () => splitPhoneNumber(value ?? '', countryName).country,
    [value, countryName]
  );
  const localPhoneNumber = useMemo(() => splitPhoneNumber(value ?? '').number, [value]);

  // Handle country and phone number change
  const handleCountryCodeChange = (country: Country) => {
    setCountryName(country.name);

    onChange(
      {
        target: {
          name,
          value: country.dial_code + localPhoneNumber,
          checkValidity: () => localPhoneNumber.length > 0,
        },
      },
      country.code
    );
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      target: {
        name,
        value: countryCode.dial_code + event.target.value,
        checkValidity: () => event.target.value.length > 0,
      },
    });
  };

  // Set default country to shop's country
  const defaultCountry = countries.find((country) => country.name === shopCountry);

  useEffect(() => {
    if (defaultCountry && countryCode.name === emptyCountry.name) {
      onChange(
        {
          target: {
            name,
            value: defaultCountry.dial_code + localPhoneNumber,
            checkValidity: () => localPhoneNumber.length > 0,
          },
        },
        defaultCountry.code
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  return (
    <PhoneInputContainer error={error} disabled={disabled}>
      <Listbox
        as={CustomSelect}
        value={countryCode}
        by="code"
        onChange={handleCountryCodeChange}
        className="focus-visible"
        disabled={disabled}
      >
        <Listbox.Button as={CustomSelectButton} error={error}>
          <span>{countryCode.emoji}</span>

          <DownArrow
            style={{
              width: 'auto',
              height: '10px',
              top: '13px',
              right: '12px',
              position: 'absolute',
              pointerEvents: 'none',
            }}
          />
        </Listbox.Button>

        <Listbox.Options as={CustomSelectOptions}>
          {countries.map((country) => (
            <Listbox.Option as={CustomSelectOption} key={country.code} value={country}>
              {country.emoji} {country.name} ({country.dial_code})
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>

      <Label opacity={disabled ? 0.35 : 0.5}>{countryCode.dial_code}</Label>

      <PhoneNumberInput
        type="tel"
        name={name}
        error={error}
        onChange={handlePhoneNumberChange}
        onBlur={onBlur}
        value={localPhoneNumber}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autocomplete="tel"
      />
    </PhoneInputContainer>
  );
};

export default PhoneInput;
